// Dip PDP — Laundry buy module.
// Drives the signed-off comp (docs/mockups/dip-laundry-pdp.html) dynamically off
// the real product variants + native Shopify/Recharge selling plans (no hardcoded
// prices). Integrates with Horizon's cart: POSTs items[] to Theme.routes.cart_add_url
// and dispatches CartAddEvent so the cart icon/drawer update like native add-to-cart.

import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { initGallery } from '@theme/dip-gallery';
// Shared with the homepage bundle builder: one <dip-tabs>, one <dip-quotes>, defined once.
import '@theme/dip-tabs';
import '@theme/dip-quotes';
import { initStickyBar } from '@theme/dip-sticky-bar';

/**
 * Read the basket's item count out of rendered section HTML.
 *
 * snippets/cart-bubble.liquid prints cart.item_count into [data-testid="cart-bubble"],
 * and every add already asks the Section Rendering API for the header, so the count
 * arrives in the SAME response as the drawer markup. Using it means an add is one
 * round trip: the drawer can open immediately instead of waiting on a second
 * GET /cart.js (which read as a lag, then a snap).
 *
 * Duplicated byte-identically in dip-buy-box.js, dip-home.js and dip-cart.js, the
 * same convention the .dip-reveal observer uses. Keep the three copies in sync.
 *
 * @param {Record<string, string> | undefined} sections
 * @returns {number | null}
 */
const dipCartCountFromSections = (sections) => {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const match = html.match(/data-testid="cart-bubble"[^>]*>\s*([\d,]+)/);
    if (match) return parseInt(match[1].replace(/,/g, ''), 10);
  }
  return null;
};

/**
 * One welcome kit per basket. Kit lines (bottle, cloth, the gift pack) are marked
 * `_kit: 1` by #lineItems; any that already sit in the cart are dropped from the POST,
 * and the marker is stripped before the request either way. Without this, a second
 * bundle add doubles the hardware and the discounts come apart at checkout: the BXGY
 * frees an exact quantity, and Shopify allocates it to the CHEAPEST eligible units, so
 * with 2 bottles + 2 cloths in the cart both cloths go free and both £15 bottles BILL,
 * while the Basic gift code (FREE-MSC-LD*) zeroes the WHOLE pack line, gifting a second
 * pack that was never offered (Charlie's store review 2, item 10).
 * Duplicated byte-identically in dip-buy-box.js and dip-home.js; keep the copies in sync.
 *
 * @param {Array<{id: number, quantity: number, _kit?: number}>} items
 * @returns {Promise<Array<{id: number, quantity: number}>>}
 */
const dipDropKitDupes = async (items) => {
  if (items.some((it) => it._kit)) {
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const inCart = new Set((cart.items || []).map((it) => Number(it.variant_id)));
      items = items.filter((it) => !(it._kit && inCart.has(Number(it.id))));
    } catch { /* offline or blocked: send as built rather than lose the sale */ }
  }
  return items.map(({ _kit, ...line }) => line);
};

// Active presentment currency + locale, injected by Shopify on the storefront.
const CURRENCY = window.Shopify?.currency?.active || 'GBP';
const LOCALE = document.documentElement.lang || 'en';
const _fmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY });
// EVERY price that reaches JS comes from Liquid (variant.price, per_delivery_price), and Shopify
// stores those as amount x 100 for every currency, including zero-decimal ones (JPY 1,000 = 100000).
// So the divisor is always 100 — never derive it from the currency's fraction digits, which would
// inflate zero-decimal currencies 100x. Intl still drops the decimals when the currency has none.
const MINOR = 100;
const money = (minorUnits) => _fmt.format((minorUnits || 0) / MINOR);
const _fmt0 = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY, minimumFractionDigits: 0, maximumFractionDigits: 0 });
// Gift "worth" values read cleaner without decimals (£35, £15) — drop them only when the amount is whole.
const moneyTidy = (minorUnits) => ((minorUnits || 0) % MINOR === 0 ? _fmt0 : _fmt).format((minorUnits || 0) / MINOR);
const perMonth = (raw, months) => money(raw / (months || 1));

// ---- Inline icons (colour via currentColor so CSS themes them) ----
const I = {
  giftOk: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8 12.2l2.6 2.6L16 9.5"/></svg>',
  lock: '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
  lockSm: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
  star: '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 2l1.6 3.6L13.5 6l-2.8 2.6L11.4 13 8 10.9 4.6 13l.7-4.4L2.5 6l3.9-.4z" stroke="currentColor" stroke-width="1.3"/></svg>',
  checkMini: '<svg width="9" height="9" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6l3 3 5-6" stroke="currentColor" stroke-width="2" fill="none"/></svg>',
};

class DipBuyBox extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-dip-data]');
    if (!dataEl) return;
    try {
      this.model = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[dip-buy-box] bad data island', e);
      return; // resting (server-rendered) DOM stays visible
    }

    this.threshold = Number(this.dataset.threshold) || 60;
    this.category = this.dataset.giftCategory || 'laundry';
    // Which line unlocks the welcome kit: 'main' (laundry PDP — main size >= threshold) or
    // 'addon' (dishwasher PDP — the laundry ADD-ON ticked at >= threshold washes).
    this.giftSource = this.dataset.giftSource || 'main';
    // Optioned-add-on mode: the add-on carries its own scent + size (dishwasher PDP's laundry add-on).
    this.addonMode = !!this.model.addon;
    // Recap / summary product names — settings-fed via the data island (defaults = legacy strings).
    this.labels = this.model.labels || {};

    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => {
      const k = el.getAttribute('ref');
      if (!(k in this.refs)) this.refs[k] = el;
    });

    this.form = this.refs.form;
    this.scentRadios = [...this.querySelectorAll('input[name="dip-scent"]')];
    this.sizeRadios = [...this.querySelectorAll('input[name="dip-size"]')];
    this.addonScentRadios = [...this.querySelectorAll('input[name="dip-addon-scent"]')];

    // Index variants and config
    this.byWashes = this.#fromConfig(this.model.sizeConfig);
    this.scentColours = this.model.scentColours || {};

    // Initial state from the server-rendered checked radios
    this.scent = this.scentRadios.find((r) => r.checked)?.value || this.model.scents?.[0] || '';
    this.size = this.sizeRadios.find((r) => r.checked)?.value || this.dataset.defaultSize || this.model.sizes[0];
    this.months = this.#defaultMonths(this.size);
    // Until the shopper picks a frequency, each size card quotes its own default cadence (a menu
    // price). After that, every card follows the cadence they chose. Picking a size resets both.
    this.freqTouched = false;
    this.qty = 1;
    this.fab = !!this.model.fabcon && !this.model.addonDefaultOff;
    this.msc = !!this.model.msc;
    // Pages without any welcome kit (fabcon PDP): no gift products, no gift sub — hide all kit UI.
    this.hasKit = !!(this.model.gift?.bottleVariantId || this.model.gift?.clothVariantId || this.model.msc);
    // Gift-subscription slot: legacy = one variant (MSC refill); MSC PDP = laundry gift with scents.
    this.giftScent = this.model.msc?.variants?.[0]?.scent || '';
    // Scaled-gift mode (laundry + dishwasher, bundle-builder parity): the free pack grows with the
    // plan that earns it and renews on the shopper's own cadence instead of one fixed frequency.
    // Blank sizeMap keeps the single fixed pack, which is what the MSC PDP wants.
    this.giftSizeMap = this.model.msc?.sizeMap || null;
    try { this.mscCodes = JSON.parse(this.dataset.mscCodes || '{}') || {}; } catch { this.mscCodes = {}; }
    // kitConfig (per-size bottle/cloth quantities) switches hardware to "always" mode: added at every
    // size, on subscriptions AND one-time orders (the MSC bundle); threshold then gates only the gift.
    this.kitConfig = this.model.kitConfig || null;
    this.alwaysKit = !!this.kitConfig;
    // Whole-kit opt-out (2026-07-19): a master toggle lets the shopper decline the free kit
    // entirely. Legacy kit mode only; always-kit hardware (MSC PDP) is part of the offer itself.
    this.kit = true;
    // Either control counts: the keep/drop choice moved into the kit <summary> (`kitTick`) so it no
    // longer requires opening the dropdown. `kitMaster` is kept for any template still rendering the
    // older in-body row.
    this.kitRemovable = !!(this.refs.kitTick || this.refs.kitMaster) && !this.alwaysKit;
    this.onceAdded = false;
    if (this.addonMode) {
      const a = this.model.addon;
      this.fabScent = this.addonScentRadios.find((r) => r.checked)?.value || a.scents?.[0] || '';
      this.fabSize = (a.defaultSize && a.sizes.includes(a.defaultSize)) ? a.defaultSize : a.sizes[0];
      // Until the shopper picks an add-on size themselves, the add-on follows the main size
      // (360 dishwasher → 360 laundry etc.). A manual pick makes their choice sticky.
      this.fabSizeTouched = false;
    }

    this.#bind();
    this.#initGiftScents();
    this.#renderFromPrice();
    this.render();
    this.#initGallery();
    this.#initCarousel();
    this.#initQuotes();
    this.#initHowLink();
    this.#initTabsets();
    this.#initSticky();
    this.#initSummary();
    this.#publishMscGuardConfig();
  }

  // dip-msc-guard.js (global, every page) owns applying/revoking the first-pack-free codes, but only
  // this component knows the product model. Publish what the guard needs into the multi-offer map
  // `dip:offers` (keyed by code so pages merge instead of clobbering): the code, the qualifying
  // (>= threshold) trigger variant ids, and the gift/reward variant ids. Each page also remembers
  // which code it last published (per product id) so blanking the section setting removes exactly its
  // own offer — the kill-switch the single-key design had. The legacy `dip:msc-code` key is removed
  // (migrated); the guard still reads it for visitors who haven't been back since.
  #publishMscGuardConfig() {
    const OFFERS = 'dip:offers';
    const SLOT = `dip:offer-slot:${this.dataset.productId}`;
    try {
      localStorage.removeItem('dip:msc-code');
      let map;
      try { map = JSON.parse(localStorage.getItem(OFFERS) || '{}') || {}; } catch { map = {}; }
      // Drop whatever this surface published last time (single code, or the CSV list written below).
      const prevList = (localStorage.getItem(SLOT) || '').split(',').filter(Boolean);
      prevList.forEach((c) => { delete map[c]; });
      const code = this.dataset.mscCode;
      const codes = this.mscCodes || {};
      // Ladder mode: one offer per giftable pack size, exactly as the bundle builder publishes them.
      const scaled = !!(this.giftSizeMap && Object.keys(codes).length);
      const giftVariants = (this.model.msc?.variants || []).filter((v) => this.#giftPlan(v));
      if ((!code && !scaled) || !giftVariants.length) {
        localStorage.setItem(OFFERS, JSON.stringify(map));
        localStorage.removeItem(SLOT);
        return;
      }
      // The trigger pool: laundry PDP = the main product; dishwasher PDP = the optioned laundry
      // add-on; MSC PDP = the main product's 2-pack/6-pack tiers (threshold 2).
      const pool = this.giftSource === 'addon' ? (this.model.addon?.variants || []) : this.model.variants;
      const trigger = pool.filter((v) => v.washes >= this.threshold).map((v) => Number(v.id));
      // Every gift is a subscription line (the pack renews after its free first cycle), so the guard
      // needs the reward present as a sub. Priority 10: this is the headline gift on the page, and it
      // must beat the paid-kit codes below on any cart where both could apply.
      const mine = [];
      if (scaled) {
        // Each size's code rewards ONLY that pack's variant, and the bigger pack outranks the smaller,
        // so the guard zeroes the pack the shopper was actually shown. Codes are variant-restricted:
        // point one at the wrong variant and it reads inapplicable while the UI still says free.
        giftVariants.forEach((v) => {
          const c = codes[v.size];
          if (!c) return;
          map[c.toUpperCase()] = {
            code: c, trigger, reward: [Number(v.id)], rewardSub: true,
            priority: 10 + (Number(v.packs) || 0),
            // Basic 100% code: it zeroes the WHOLE reward line whatever its quantity, so the
            // guard clamps a manually-bumped reward line back to 1. NEVER set this on a BXGY
            // offer (the paid-kit codes below): their reward legitimately carries qty > 1 and
            // a clamp breaks the full-get-quantity rule (bug found 2026-08-13, MSC one-time).
            clampReward: true,
          };
          mine.push(c.toUpperCase());
        });
      } else {
        // Priority 20, ABOVE the pack ladder (10 + packs). A surface with a single fixed gift has the
        // roles inverted: here the pack is what you buy and the laundry sub is the gift, which is
        // precisely the cart shape the ladder's offers look for (laundry sub present, pack sub present).
        // Left at 10 the ladder outranked this code on the MSC page and billed the "free" laundry pack
        // in full. The reverse cannot happen: the ladder's own carts never hold both a 2-or-6-pack sub
        // and a laundry 60 sub, because 60 washes earns a 1-pack.
        map[code.toUpperCase()] = { code, trigger, reward: giftVariants.map((v) => Number(v.id)), rewardSub: true, priority: 20, clampReward: true };
        mine.push(code.toUpperCase());
      }

      // Kit-always surfaces (the MSC PDP: bottle + cloth ship with EVERY kit, laundry or not) need the
      // paid-kit codes too, because the FREE-KIT-MSC-* automatics that used to zero the hardware were
      // retired on 2026-07-27. One code per pack size, triggered by that size and rewarding the
      // hardware. Priority 1, so a laundry welcome cart still hands the slot to the gift code above and
      // lets the FREE-GIFT-LD1 automatic cover the hardware instead.
      let kitCodes = {};
      try { kitCodes = JSON.parse(this.dataset.kitCodes || '{}') || {}; } catch { kitCodes = {}; }
      const g = this.model.gift || {};
      const hardware = [Number(g.bottleVariantId) || null, Number(g.clothVariantId) || null].filter(Boolean);
      if (this.alwaysKit && hardware.length) {
        (this.model.variants || []).forEach((v) => {
          const kc = kitCodes[v.size];
          if (!kc) return;
          map[kc.toUpperCase()] = {
            code: kc,
            trigger: [Number(v.id)],
            triggerSub: false,   // the pack may be bought one-time or on subscription
            reward: hardware,
            rewardSub: false,    // bottle + cloth always ride as one-time lines
            priority: 1,
          };
          mine.push(kc.toUpperCase());
        });
      } else if (hardware.length) {
        // Ladder surfaces (laundry and dishwasher PDPs) need the paid-kit codes too: if the
        // shopper removes the laundry line in the basket, the welcome kit's pack + hardware
        // stay behind as a paid kit, and without these offers a PDP-only session has nothing
        // in the registry to swap to, so the hardware bills in full (QA F1, 2026-08-14: the
        // bundle and MSC PDP published them, these pages did not). Keyed off the GIFT pack
        // variants, because that is the line the shopper's cart actually holds; blockedBy the
        // laundry trigger pool so a welcome cart still hands the slot to the ladder code above
        // and FREE-GIFT-LD1 covers the hardware.
        giftVariants.forEach((v) => {
          const kc = kitCodes[v.size];
          if (!kc) return;
          map[kc.toUpperCase()] = {
            code: kc,
            trigger: [Number(v.id)],
            triggerSub: false,   // the pack survives as a sub line today, but do not depend on it
            reward: hardware,
            rewardSub: false,    // bottle + cloth always ride as one-time lines
            blockedBy: trigger,
            priority: 1,
          };
          mine.push(kc.toUpperCase());
        });
      }

      localStorage.setItem(OFFERS, JSON.stringify(map));
      if (mine.length) localStorage.setItem(SLOT, mine.join(','));
      else localStorage.removeItem(SLOT);
    } catch { /* storage unavailable (private mode) — guard simply stays inert */ }
  }

  #fromConfig(cfg) {
    // Map a size-value string ("60 washes") to its washes integer for matching the fabcon/MSC by washes.
    const out = {};
    (this.model.sizes || []).forEach((sz) => {
      const w = parseInt(String(sz).replace(/[^0-9]/g, ''), 10) || 0;
      out[sz] = w;
    });
    return out;
  }

  #cfg(size) {
    return (this.model.sizeConfig && this.model.sizeConfig[size]) || {};
  }
  #defaultMonths(size) {
    const c = this.#cfg(size);
    if (c.default) return c.default;
    // fall back to the smallest available cadence on that variant
    const v = this.#variant(this.scent, size);
    const months = v ? Object.keys(v.plans).map(Number).sort((a, b) => a - b) : [1];
    return months[0] || 1;
  }
  #monthsFor(size) {
    const c = this.#cfg(size);
    if (Array.isArray(c.months) && c.months.length) return c.months;
    const v = this.#variant(this.scent, size);
    return v ? Object.keys(v.plans).map(Number).sort((a, b) => a - b) : [1];
  }

  #variant(scent, size) {
    return this.model.variants.find((v) => (scent ? v.scent === scent : true) && v.size === size)
      || this.model.variants.find((v) => v.size === size)
      || this.model.variants[0];
  }
  #fabconVariant(size) {
    const w = this.byWashes[size];
    return this.model.fabcon?.byWashes?.[w] || null;
  }
  // The current add-on variant: optioned mode resolves the shopper's own scent × size pick
  // (riding the shared cadence); legacy mode keeps the fabcon washes-match to the main size.
  #addonVariant() {
    if (this.addonMode) {
      const a = this.model.addon;
      return a.variants.find((v) => (this.fabScent ? v.scent === this.fabScent : true) && v.size === this.fabSize)
        || a.variants.find((v) => v.size === this.fabSize)
        || null;
    }
    return this.#fabconVariant(this.size);
  }
  // The add-on size whose washes match a given main size ("120 Washes" → "120 washes"), if any.
  #matchedAddonSize(size) {
    const w = this.#washes(size);
    if (!w) return null;
    return (this.model.addon?.sizes || []).find((sz) => (parseInt(String(sz).replace(/[^0-9]/g, ''), 10) || 0) === w) || null;
  }
  // Follow the main size with the add-on size unless the shopper has picked one manually.
  // Charlie 2026-07-30: the main size drives the add-on size, full stop. A manual add-on pick used to
  // stick forever (`fabSizeTouched`), so anyone who had touched that dropdown then chose the annual
  // dishwasher tier kept a 60-wash laundry line and a one-pack gift, which contradicts the rule the
  // page is meant to follow. The manual choice still holds for the size they picked it on; changing the
  // main size re-syncs and they can change it again.
  #syncAddonSize() {
    if (!this.addonMode) return;
    const match = this.#matchedAddonSize(this.size);
    if (match) { this.fabSize = match; this.fabSizeTouched = false; }
  }

  // The gift-subscription variant: the right pack size for the plan (scaled mode) at the chosen
  // gift scent (MSC PDP). Legacy surfaces have one variant and both filters fall through.
  #giftVariant() {
    const m = this.model.msc;
    if (!m || !m.variants?.length) return null;
    const size = this.#giftSizeFor();
    const sized = size ? m.variants.filter((v) => v.size === size) : [];
    const pool = sized.length ? sized : m.variants;
    return pool.find((v) => (this.giftScent ? v.scent === this.giftScent : true)) || pool[0];
  }

  // The code that zeroes a given gift size. Surfaces with a per-size ladder answer from the map ONLY,
  // so a size we hold no code for reports none and is never dressed up as free.
  #mscCodeFor(size) {
    const codes = this.mscCodes || {};
    if (Object.keys(codes).length) return codes[size] || null;
    return this.dataset.mscCode || null;
  }

  // Which pack size the plan earns. The washes come from whichever line the gift is gated on, so on
  // the dishwasher PDP the dishwasher size drives the laundry add-on and the add-on drives this.
  // A size with no usable code steps DOWN to the largest one that has one: better a smaller genuine
  // gift than a "free" pack that quietly bills in full (the bundle's 2026-07-28 lesson).
  #giftSizeFor() {
    if (!this.giftSizeMap) return null;
    const variants = this.model.msc?.variants || [];
    const coded = variants
      .filter((v) => this.#giftPlan(v) && this.#mscCodeFor(v.size))
      .sort((a, b) => (Number(a.packs) || 0) - (Number(b.packs) || 0));
    if (!coded.length) return variants[0]?.size || null;
    const wanted = this.giftSizeMap[String(this.#giftWashes())];
    if (!wanted) return coded[0].size; // no rung at this size (below the threshold): the entry pack
    if (coded.some((v) => v.size === wanted)) return wanted;
    const wantedPacks = Number(variants.find((v) => v.size === wanted)?.packs) || 0;
    const below = coded.filter((v) => (Number(v.packs) || 0) <= wantedPacks);
    return (below.length ? below[below.length - 1] : coded[0]).size;
  }

  // The gift's selling plan. Scaled mode matches the shopper's cadence so the pack arrives in step
  // with the plan it came with; otherwise it is the fixed msc_months plan resolved server-side.
  #giftPlan(v) {
    if (!v) return null;
    const fixed = Number(this.model.msc?.months) || 2;
    if (this.giftSizeMap && v.plans) {
      const own = v.plans[this.months];
      if (own?.id) return { id: own.id, raw: own.raw, months: this.months };
      const back = v.plans[fixed];
      return back?.id ? { id: back.id, raw: back.raw, months: fixed } : null;
    }
    return v.planId ? { id: v.planId, raw: v.raw, months: fixed } : null;
  }
  // Hardware quantities for the current size (always-kit mode only).
  #hwQty() {
    if (!this.alwaysKit) return null;
    const q = this.kitConfig[this.size] || {};
    return { bottle: Number(q.bottle) || 0, cloth: Number(q.cloth) || 0 };
  }
  // Gift scent picker (only when the gift product offers scents, e.g. the MSC PDP's laundry gift).
  #initGiftScents() {
    const wrap = this.refs.giftScents;
    const m = this.model.msc;
    if (!wrap || !m || !m.variants?.length) return;
    // Pills are for a genuine scent choice. In scaled-gift mode the variant list is every PACK SIZE of
    // a single-scent product, so counting variants would build a row of blank pills.
    const scents = [...new Set(m.variants.map((v) => v.scent).filter(Boolean))];
    if (scents.length < 2) return;
    wrap.hidden = false;
    scents.forEach((scent) => {
      const label = document.createElement('label');
      label.className = 'dip-scent-pill dip-scent-pill--sm';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'dip-gift-scent';
      input.value = scent;
      if (scent === this.giftScent) input.checked = true;
      input.addEventListener('change', () => {
        if (input.checked) { this.giftScent = scent; this.onceAdded = false; this.render(); }
      });
      const sw = document.createElement('span');
      sw.className = 'dip-scent-sw';
      sw.setAttribute('aria-hidden', 'true');
      label.append(input, sw, document.createTextNode(scent));
      wrap.appendChild(label);
    });
    this.giftScentPills = [...wrap.querySelectorAll('input[name="dip-gift-scent"]')];
  }

  // The washes figure the welcome kit is gated on (gift_source setting).
  #giftWashes() {
    if (this.giftSource === 'addon') {
      if (!this.fab) return 0;
      return this.#addonVariant()?.washes || 0;
    }
    return this.#washes(this.size);
  }
  #washes(size) {
    return this.byWashes[size] || 0;
  }

  #monthLabel(m) {
    return m === 1 ? 'Monthly' : `Every ${m} months`;
  }
  #cadence(m) {
    return m === 1 ? 'every month' : m === 12 ? 'once a year' : `every ${m} months`;
  }

  // ---- Events ----
  #bind() {
    this.scentRadios.forEach((r) => r.addEventListener('change', () => { if (r.checked) { this.scent = r.value; this.onceAdded = false; this.render(); } }));
    this.sizeRadios.forEach((r) => r.addEventListener('change', () => {
      if (r.checked) { this.size = r.value; this.months = this.#defaultMonths(this.size); this.freqTouched = false; this.#syncAddonSize(); this.onceAdded = false; this.render(); }
    }));
    this.refs.freq?.addEventListener('change', (e) => {
      this.months = Number(e.target.selectedOptions[0]?.dataset.months) || this.months;
      this.freqTouched = true;
      this.onceAdded = false; this.render();
    });
    this.refs.fab?.addEventListener('click', () => { this.fab = !this.fab; this.onceAdded = false; this.render(); });
    this.addonScentRadios.forEach((r) => r.addEventListener('change', () => { if (r.checked) { this.fabScent = r.value; this.onceAdded = false; this.render(); } }));
    this.refs.addonSize?.addEventListener('change', (e) => { this.fabSize = e.target.value; this.fabSizeTouched = true; this.onceAdded = false; this.render(); });
    this.refs.msc?.addEventListener('click', () => {
      if (this.refs.msc.classList.contains('is-locked')) return; // gift not offered at this size
      if (this.kitRemovable && !this.kit) return; // kit declined: re-include it first
      this.msc = !this.msc; this.onceAdded = false; this.render();
    });
    const toggleKit = (e) => {
      // The tick sits inside <summary>, so its click must not reach the disclosure.
      e?.preventDefault(); e?.stopPropagation();
      if (this.refs.kit?.classList.contains('is-locked')) return; // kit locked at this size
      this.kit = !this.kit; this.onceAdded = false; this.render();
    };
    this.refs.kitTick?.addEventListener('click', toggleKit);
    this.refs.kitMaster?.addEventListener('click', () => toggleKit());
    this.refs.qMinus?.addEventListener('click', () => { this.qty = Math.max(1, this.qty - 1); this.onceAdded = false; this.render(); });
    this.refs.qPlus?.addEventListener('click', () => { this.qty = Math.min(10, this.qty + 1); this.onceAdded = false; this.render(); });

    // (The closing CTA's dip:preselect / dip:addplan listeners were removed with its plan
    // snapshot on 2026-07-25 — that card carried a typed price. The CTA now just scrolls
    // back to #dip-plans and the shopper picks here, where every price is live.)

    this.#initKitFlip();

    this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.#addToCart(true); });
    this.refs.once?.addEventListener('click', () => this.#addToCart(false));
    this.refs.sbCta?.addEventListener('click', () => this.#addToCart(true));
  }

  // ---- Render ----
  render() {
    const variant = this.#variant(this.scent, this.size);
    if (!variant) return;
    this.variant = variant;
    const ok = this.#giftWashes() >= this.threshold;
    const m = this.months;
    const plan = variant.plans[m];
    const L = this.labels;

    if (this.refs.variantId) this.refs.variantId.value = variant.id;

    // --- Scent re-theming ---
    const sc = this.scentColours[this.scent];
    if (sc) {
      this.style.setProperty('--sel', sc.sel || '#F4F7FB');
      this.style.setProperty('--sel-ink', sc.selInk || 'var(--dip-accent-ink)');
    }
    this.scentRadios.forEach((r) => {
      const label = r.closest('.dip-scent-pill');
      const col = this.scentColours[r.value] || {};
      label.style.setProperty('--st', col.fill || 'var(--dip-accent)');
      label.style.setProperty('--stb', col.border || 'var(--dip-accent-ink)');
      const on = r.checked;
      label.classList.toggle('is-selected', on);
      const sw = label.querySelector('.dip-scent-sw');
      if (sw) sw.innerHTML = on ? I.checkMini : '';
    });

    // --- Size tiers ---
    this.sizeRadios.forEach((r) => {
      const label = r.closest('.dip-tier');
      const on = r.value === this.size;
      label.classList.toggle('is-selected', on);
      const cfg = this.#cfg(r.value);
      const v = this.#variant(this.scent, r.value);
      const dm = cfg.default || (v ? Object.keys(v.plans).map(Number).sort((a, b) => a - b)[0] : 1);
      // Once the shopper has touched the frequency, every card quotes /mo at the cadence they chose
      // (when this size offers it), not at its own default — otherwise the add-on row repriced while
      // the size cards next to it stayed frozen, which read as a bug.
      const allowed = !Array.isArray(cfg.months) || cfg.months.includes(this.months);
      const cm = this.freqTouched && allowed && v?.plans[this.months] ? this.months : dm;
      const dplan = v?.plans[cm];
      const roleEl = label.querySelector('[data-role]');
      const popular = (cfg.label || '').toLowerCase() === 'most popular';
      label.classList.toggle('is-popular', popular);
      if (roleEl) roleEl.textContent = cfg.label || '';
      const permoEl = label.querySelector('[data-permo]');
      if (permoEl && dplan) permoEl.innerHTML = `${perMonth(dplan.raw, cm)}<small>/mo</small>`;
      // The amount this size charges and how often, under the per-month figure it is derived from. A
      // menu price: this size only, no add-on and no quantity, so the cards stay comparable while the
      // price block below them carries the real basket.
      const billedEl = label.querySelector('[data-billed]');
      if (billedEl) billedEl.textContent = dplan ? `billed ${money(dplan.raw)} ${this.#cadence(cm)}` : '';
      const flexEl = label.querySelector('[data-flex]');
      if (flexEl) {
        if (cfg.perWash && v) {
          // Per-wash off the subscription per-delivery price (the figure that beats the high street).
          const perWashRaw = (dplan ? dplan.raw : v.otRaw) / this.#washes(r.value);
          flexEl.hidden = false;
          flexEl.textContent = `Just ${money(perWashRaw)}/wash · ${L.perWashNote || 'beats the high street'}`;
        } else flexEl.hidden = true;
      }
    });

    // --- Frequency ---
    if (this.refs.freq) {
      const opts = this.#monthsFor(this.size);
      this.refs.freq.innerHTML = '';
      opts.forEach((mm) => {
        const o = document.createElement('option');
        o.value = variant.plans[mm]?.id || '';
        o.dataset.months = mm;
        o.textContent = this.#monthLabel(mm);
        if (mm === m) o.selected = true;
        this.refs.freq.appendChild(o);
      });
    }

    // --- Add-on (legacy: fabcon washes-matched; optioned: shopper-picked scent × size) ---
    let fabSubRaw = 0, fabOtRaw = 0, fabVariant = null, fabOn = false;
    if (this.model.fabcon || this.addonMode) {
      fabVariant = this.#addonVariant();
      fabOn = this.fab && !!fabVariant;
      if (fabVariant) {
        fabSubRaw = fabVariant.plans[m]?.raw ?? fabVariant.otRaw;
        fabOtRaw = fabVariant.otRaw;
      }
      if (this.refs.fab) {
        this.refs.fab.setAttribute('aria-pressed', this.fab ? 'true' : 'false');
        this.refs.fab.classList.toggle('is-selected', this.fab);
      }
      if (this.refs.fabPermo && fabVariant) this.refs.fabPermo.innerHTML = `${perMonth(fabSubRaw, m)}<small>/mo</small>`;
      // What the add-on actually charges, on the same cadence as the plan it rides.
      if (this.refs.fabBilled) this.refs.fabBilled.textContent = fabVariant ? `billed ${money(fabSubRaw)} ${this.#cadence(m)}` : '';

      // Optioned add-on: scent pills + size dropdown live inside the add-on row.
      if (this.addonMode) {
        if (this.refs.addonOpts) this.refs.addonOpts.hidden = !this.fab;
        this.addonScentRadios.forEach((r) => {
          const label = r.closest('.dip-scent-pill');
          if (!label) return;
          const col = this.scentColours[r.value] || {};
          label.style.setProperty('--st', col.fill || 'var(--dip-accent)');
          label.style.setProperty('--stb', col.border || 'var(--dip-accent-ink)');
          const on = r.value === this.fabScent;
          r.checked = on;
          label.classList.toggle('is-selected', on);
          const sw = label.querySelector('.dip-scent-sw');
          if (sw) sw.innerHTML = on ? I.checkMini : '';
        });
        if (this.refs.addonSize) {
          const sel = this.refs.addonSize;
          sel.innerHTML = '';
          this.model.addon.sizes.forEach((sz) => {
            const av = this.model.addon.variants.find((v) => (this.fabScent ? v.scent === this.fabScent : true) && v.size === sz)
              || this.model.addon.variants.find((v) => v.size === sz);
            const raw = av ? (av.plans[m]?.raw ?? av.otRaw) : 0;
            const o = document.createElement('option');
            o.value = sz;
            o.textContent = av ? `${sz} · ${perMonth(raw, m)}/mo` : sz;
            if (sz === this.fabSize) o.selected = true;
            sel.appendChild(o);
          });
        }
      }
    }

    // --- Quantity ---
    if (this.refs.qty) this.refs.qty.textContent = this.qty;

    // --- Offer strip + welcome kit ---
    const hq = this.#hwQty(); // null in legacy mode; {bottle, cloth} quantities in always-kit mode
    const bottle = this.model.gift?.bottleValue || 0;
    const cloth = this.model.gift?.clothValue || 0;
    const bottleWorth = bottle * (hq ? hq.bottle : 1);
    const clothWorth = cloth * (hq ? hq.cloth : 1);
    const gv = this.#giftVariant();
    const giftPlan = this.#giftPlan(gv);
    // Worth is the pack's SUBSCRIPTION per-delivery price at the cadence it will actually renew on,
    // never its one-time price.
    const mscRaw = giftPlan?.raw ?? 0;
    // Pack count is only a pack count on the ladder. Elsewhere the size string counts something else
    // (the MSC PDP gifts "60 washes" of laundry), so no surface but the ladder gets a "N×" prefix.
    const giftPacks = this.giftSizeMap ? (Number(gv?.packs) || 0) : 0;
    const giftAvailable = !!(gv && giftPlan);
    // Only a pack we hold a code for is free, so only that one counts towards the kit's worth or
    // wears a FREE flag. Everything below reads giftFree, not giftAvailable.
    const giftFree = giftAvailable && !!this.#mscCodeFor(gv.size);
    const kitWorthOn = bottleWorth + clothWorth + (ok && this.msc && giftFree ? mscRaw : 0);
    const kitWorthFull = bottleWorth + clothWorth + (giftFree ? mscRaw : 0);

    // Whole-kit opt-out: wantKit is the shopper's include/exclude choice, layered ON TOP of the
    // unlock gate (ok). Pages without the master toggle (alwaysKit, or kit_removable off) always want.
    const wantKit = this.kit || !this.kitRemovable;
    const kitNoun = L.kitNoun || 'cleaning kit';
    const hintOn = (L.hintOn || 'included with this plan at [n] washes or more').replace('[n]', String(this.threshold));
    const hintLocked = (L.hintLocked || 'choose [n] washes or more to unlock').replace('[n]', String(this.threshold));
    const hintRemoved = L.hintRemoved || 'removed from this order, tap the kit to add it back';
    // Offer bar (the shared kitbar component). Four states, same as the strip it replaced: no kit on
    // this page at all, kit available and wanted, kit available and declined, kit still locked. The
    // heading names the money and the subline names the condition, both computed.
    if (this.refs.kitStrip) {
      const strip = this.refs.kitStrip;
      strip.hidden = !this.hasKit;
      if (this.hasKit) {
        const unlocked = this.alwaysKit || ok;
        const state = !unlocked ? 'is-locked' : (wantKit ? 'is-on' : 'is-off');
        strip.className = `dip-kitbar ${state}`;
        const worth = moneyTidy(unlocked && wantKit ? kitWorthOn : kitWorthFull);
        // Authored copy wins (Charlie 2026-07-31: "Free £35 cleaning kit" is too vague, each page should
        // name the actual items and the condition that earns them). [worth] stays computed either way,
        // so no figure can go stale. The generic string remains the fallback for an unauthored page.
        const withWorth = (t) => String(t || '').replace(/\[worth\]/g, worth);
        if (this.refs.kitBarH) {
          this.refs.kitBarH.textContent = L.offerHeading ? withWorth(L.offerHeading) : `Free ${worth} ${kitNoun}`;
        }
        if (this.refs.kitBarS) {
          const fallback = !unlocked ? hintLocked
            : !wantKit ? hintRemoved
              : (this.alwaysKit && !ok ? hintLocked : hintOn);
          // The authored line describes the unlocked offer, so the state hints still win when the kit is
          // locked or declined: those say what to DO, which matters more than what is in the box.
          this.refs.kitBarS.textContent = (unlocked && wantKit && L.offerSub) ? withWorth(L.offerSub) : fallback;
        }
        if (this.refs.kitBarFlag) this.refs.kitBarFlag.hidden = !(unlocked && wantKit);
      }
    }
    if (this.refs.kit) {
      this.refs.kit.hidden = !this.hasKit;
      this.refs.kit.className = (this.alwaysKit || ok) ? (wantKit ? 'dip-kit is-on' : 'dip-kit is-on is-off') : 'dip-kit is-locked';
    }
    if (this.refs.kitHead) {
      this.refs.kitHead.innerHTML = (this.alwaysKit || ok)
        ? (wantKit
            ? `${I.star} Your free welcome kit · worth ${moneyTidy(kitWorthOn)}`
            : `${I.star} Welcome kit removed · worth ${moneyTidy(kitWorthFull)}`)
        : `${I.lockSm} Free welcome kit · ${hintLocked}`;
    }
    if (this.refs.kitTick) this.refs.kitTick.setAttribute('aria-pressed', this.kit ? 'true' : 'false');
    // State line under the heading: says what the kit's status IS, and when it is free, what keeps it
    // free. The condition used to be visible only while the kit was locked, so it vanished exactly when
    // someone might drop the thing earning it (Charlie 2026-07-29).
    if (this.refs.kitState) {
      this.refs.kitState.textContent = !(this.alwaysKit || ok)
        ? hintLocked
        : !wantKit ? hintRemoved
          : (L.kitFreeWith || hintOn);
    }
    if (this.refs.kitMaster) {
      this.refs.kitMaster.setAttribute('aria-pressed', this.kit ? 'true' : 'false');
      this.refs.kitMaster.classList.toggle('is-selected', this.kit);
      if (this.refs.kitMasterState) {
        this.refs.kitMasterState.textContent = this.kit ? 'Included, tap to remove' : 'Removed, tap to add back';
      }
    }
    // Future-gifts ladder is a subscriber perk, so it tracks the welcome kit's
    // availability: locked (dimmed, with the unlock hint) at sizes below the kit
    // threshold, e.g. the 30-wash pack. Mirrors the kit treatment above.
    const kitOn = this.alwaysKit || ok;
    if (this.refs.future) this.refs.future.classList.toggle('is-locked', !kitOn);
    if (this.refs.futureLock) {
      this.refs.futureLock.hidden = kitOn;
      this.refs.futureLock.textContent = kitOn ? '' : hintLocked;
    }
    if (this.refs.sumFuture) this.refs.sumFuture.hidden = !kitOn;
    if (this.refs.bottleQtyTag) {
      this.refs.bottleQtyTag.hidden = !(hq && hq.bottle > 1);
      if (hq && hq.bottle > 1) this.refs.bottleQtyTag.textContent = `${hq.bottle}× `;
    }
    if (this.refs.clothQtyTag) {
      this.refs.clothQtyTag.hidden = !(hq && hq.cloth > 1);
      if (hq && hq.cloth > 1) this.refs.clothQtyTag.textContent = `${hq.cloth}× `;
    }
    if (this.refs.bottleWas) this.refs.bottleWas.textContent = moneyTidy(bottleWorth);
    if (this.refs.clothWas) this.refs.clothWas.textContent = moneyTidy(clothWorth);
    if (this.refs.mscWas) this.refs.mscWas.textContent = moneyTidy(mscRaw);
    // Name the pack count when the gift scales, so "2×" is visible before the breakdown.
    if (this.refs.mscTitle) {
      const base = this.refs.mscTitle.dataset.title || this.refs.mscTitle.textContent;
      this.refs.mscTitle.textContent = giftPacks > 1 ? `${giftPacks}× ${base}` : base;
    }
    if (this.refs.msc) {
      // Always-kit mode: the gift row locks (not toggles) at sizes below the threshold.
      const giftLocked = this.alwaysKit && !ok;
      this.refs.msc.setAttribute('aria-pressed', this.msc ? 'true' : 'false');
      this.refs.msc.classList.toggle('is-selected', this.msc && !giftLocked);
      this.refs.msc.classList.toggle('is-locked', giftLocked);
      this.refs.msc.setAttribute('aria-disabled', giftLocked ? 'true' : 'false');
      if (this.refs.mscFree) this.refs.mscFree.style.display = (this.msc && !giftLocked) ? 'inline-flex' : 'none';
      if (this.refs.mscState) {
        this.refs.mscState.textContent = giftLocked
          ? (L.mscLocked || 'not included at this size')
          : (this.msc ? 'Included, tap to remove' : 'Not included, tap to add');
      }
      if (this.refs.giftScents) this.refs.giftScents.hidden = (this.giftScentPills || []).length < 2 || giftLocked || !this.msc || !wantKit;
    }
    // Gift scent pills: theme + selection state (mirrors the scent selectors).
    (this.giftScentPills || []).forEach((r) => {
      const label = r.closest('.dip-scent-pill');
      if (!label) return;
      const col = this.scentColours[r.value] || {};
      label.style.setProperty('--st', col.fill || 'var(--dip-accent)');
      label.style.setProperty('--stb', col.border || 'var(--dip-accent-ink)');
      const on = r.value === this.giftScent;
      r.checked = on;
      label.classList.toggle('is-selected', on);
      const sw = label.querySelector('.dip-scent-sw');
      if (sw) sw.innerHTML = on ? I.checkMini : '';
    });
    // Where the hardware ships with every order (always-kit), this note is the thing that SAYS so, and
    // it used to hide at exactly the sizes where the gift was locked, i.e. when it mattered most.
    if (this.refs.onceNote) {
      this.refs.onceNote.hidden = this.alwaysKit ? !this.hasKit : (!this.hasKit || !ok || !wantKit);
    }

    // --- Totals ---
    const detSubRaw = plan?.raw ?? variant.otRaw;
    const detOtRaw = variant.otRaw;
    // Legacy fabcon scales with the main quantity (washes-matched); an optioned add-on is one line.
    const addonMult = this.addonMode ? 1 : this.qty;
    const tSubRaw = detSubRaw * this.qty + (fabOn ? fabSubRaw * addonMult : 0);
    const tOtRaw = detOtRaw * this.qty + (fabOn ? fabOtRaw * addonMult : 0);
    this.tOtRaw = tOtRaw;

    // Bold per-month figure and plain billed clause are two spans on ONE line (see .dip-total-v), so the
    // headline writes only the number and `billed` only its explanation.
    if (this.refs.headline) {
      const bold = this.refs.headline.querySelector('b');
      const html = `${perMonth(tSubRaw, m)}<small>/mo</small>`;
      if (bold) bold.innerHTML = html; else this.refs.headline.innerHTML = html;
    }
    // Same basket bought one-time, at the same cadence, so the two figures are directly comparable.
    if (this.refs.wasLine) {
      this.refs.wasLine.textContent = tOtRaw > tSubRaw ? perMonth(tOtRaw, m) : '';
    }
    if (this.refs.billed) this.refs.billed.textContent = `billed ${money(tSubRaw)} ${this.#cadence(m)}${this.qty > 1 ? ` (${this.qty} packs)` : ''}`;
    if (this.refs.mscNote) {
      if (ok && this.msc && giftFree && wantKit) {
        this.refs.mscNote.hidden = false;
        const giftMonths = giftPlan?.months || this.model.msc.months || 2;
        this.refs.mscNote.textContent = `+ ${giftPacks > 1 ? `${giftPacks}× ` : ''}${(L.msc || 'multi-surface refills').toLowerCase()} free today, then ${moneyTidy(mscRaw)} ${this.#cadence(giftMonths)}`;
      } else this.refs.mscNote.hidden = true;
    }

    // --- In-plan recap (itemises order contents; mirrors the summary modal, minus per-line prices) ---
    // Hoisted: the CTA below states this total too, so it must outlive the recap block.
    let svTotal = 0;

    if (this.refs.recapList) {
      const row = (title, detail, tag = '') => `<li><span class="dip-recap-tx"><span class="dip-recap-t">${I.checkMini}${title}${tag}</span>${detail ? `<span class="dip-recap-s">${detail}</span>` : ''}</span></li>`;
      const rows = [
        row(L.main || 'Laundry Detergent Sheets', `${this.size}${this.scent ? ` · ${this.scent}` : ''}${this.qty > 1 ? ` · ×${this.qty}` : ''}`),
      ];
      const addonSub = this.addonMode ? `${this.fabSize}${this.fabScent ? ` · ${this.fabScent}` : ''}` : (L.addonSub || 'same delivery');
      if (fabOn) rows.push(row(L.addon || 'Fabric Conditioner Sheets', addonSub));
      if (this.hasKit && (this.alwaysKit || ok) && wantKit) {
        const bits = hq
          ? [`${hq.bottle > 1 ? hq.bottle + '× ' : ''}bottle${hq.bottle > 1 ? 's' : ''}`, `${hq.cloth > 1 ? hq.cloth + '× ' : ''}cloth${hq.cloth > 1 ? 's' : ''}`]
          : ['bottle', 'cloth'];
        if (ok && this.msc && giftFree) {
          const packBit = giftPacks > 1 ? `first ${giftPacks} multi-surface packs` : 'first multi-surface pack';
          bits.push(this.alwaysKit ? `free ${(L.msc || 'gift').toLowerCase()}${this.giftScent ? ` · ${this.giftScent}` : ''}` : packBit);
        }
        rows.push(row('Welcome kit', bits.join(', '), ' <span class="dip-recap-free-tag">FREE</span>'));
      }
      this.refs.recapList.innerHTML = rows.join('');

      // Savings stack: the three savings, then the total. Same shape and wording as the bundle.
      const subSave = Math.max(0, tOtRaw - tSubRaw);
      let packSave = this.#packSaveOf(this.model.variants, variant, this.qty);
      // Optioned add-on carries a variants array; the legacy fabcon is a byWashes map.
      const addonList = this.model.addon?.variants
        || (this.model.fabcon?.byWashes ? Object.values(this.model.fabcon.byWashes) : null);
      const addonV = this.#addonVariant();
      if (fabOn && addonList && addonV) packSave += this.#packSaveOf(addonList, addonV, this.addonMode ? 1 : this.qty);
      const giftWorth = (this.hasKit && (this.alwaysKit || ok) && wantKit) ? kitWorthOn : 0;
      svTotal = subSave + packSave + giftWorth;
      if (this.refs.savings) {
        this.refs.savings.hidden = svTotal <= 0;
        const put = (rowRef, valRef, amount, fmt = money) => {
          const r = this.refs[rowRef], v = this.refs[valRef];
          if (r) r.hidden = amount <= 0;
          if (v) v.textContent = fmt(amount);
        };
        // Every row is a subtraction, so it reads as money coming off rather than as another price.
        const less = (v) => `\u2212${money(v)}`;
        if (this.refs.svSub) this.refs.svSub.textContent = less(subSave);
        put('svPackRow', 'svPack', packSave, less);
        put('svGiftRow', 'svGift', giftWorth, less);
        if (this.refs.svTotal) this.refs.svTotal.textContent = money(svTotal);
        // Contents value against what is charged. A VALUE claim (what the box is worth), never a "was"
        // price: the gift's worth and the pack-size comparison are not prices this basket was sold at.
        if (this.refs.svValue) {
          const tmpl = L.saveValueNote || '[value] of products, yours for [price]';
          this.refs.svValue.hidden = svTotal <= 0 || tSubRaw <= 0;
          this.refs.svValue.textContent = tmpl
            .replace(/\[value\]/g, moneyTidy(tSubRaw + svTotal))
            .replace(/\[price\]/g, money(tSubRaw));
        }
      }
      // One savings story per surface: the chip in the price block states the same total.
      if (this.refs.save) this.refs.save.textContent = svTotal > 0 ? `${L.saveWord || 'Save'} ${money(svTotal)} ${L.saveWhen || 'today'}` : '';
    }

    // --- CTA / one-time ---
    // The button states the charge, then the WHOLE saving that comes with it. It used to strike the
    // one-time price, which quoted the smallest of the three savings and made the same offer look
    // worse on the button than in the summary right above it (Charlie 2026-07-31). The saving is
    // stated as a saving, not as a struck price, because only part of it is a former price.
    const ctaCopy = svTotal > 0
      ? `${L.ctaWord || 'Add to basket'} · ${money(tSubRaw)} <span class="dip-cta-save">${L.saveWord || 'Save'} ${money(svTotal)}</span>`
      : `${L.ctaWord || 'Add to basket'} · ${money(tSubRaw)}`;
    if (this.refs.ctaLabel && !this.refs.ctaLabel.dataset.busy) {
      this.refs.ctaLabel.innerHTML = ctaCopy;
    }
    if (this.refs.sumAdd) this.refs.sumAdd.innerHTML = ctaCopy;
    if (this.refs.once) {
      this.refs.once.className = this.onceAdded ? 'dip-once is-added' : 'dip-once';
      this.refs.once.textContent = this.onceAdded ? `✓ One-Time Purchase (${money(tOtRaw)}) added` : `One-Time Purchase · ${money(tOtRaw)}`;
    }

    // --- Sticky bar ---
    // No struck one-time price in the floating bar (2026-08-09). Measured at 375px, the bar's
    // text column has 194px and this line needed 231px with the strike, so the /mo figure, the
    // number the shopper is actually deciding on, was being pushed onto a second line. The
    // strike still runs in the buy box above, where there is room for it, and the bar now
    // states the saving outright underneath the button, which carries the discount better than
    // a struck figure does.
    if (this.refs.sbPrice) this.refs.sbPrice.textContent = `${perMonth(tSubRaw, m)}/mo`;
    // Billing cadence must always be legible on the floating bar before checkout (not just /mo).
    if (this.refs.sbBilled) this.refs.sbBilled.textContent = `Billed ${money(tSubRaw)} ${this.#cadence(m)}${this.qty > 1 ? ` (${this.qty} packs)` : ''}`;
    if (this.refs.sbKit) this.refs.sbKit.style.display = (this.hasKit && (this.alwaysKit || ok) && wantKit) ? 'inline' : 'none';
    // The floating bar still carries the whole saving, but UNDER the button rather than inside
    // it (Charlie 2026-08-09). In the button the pill was pure width: .dip-sb-cta never shrinks,
    // so every pixel of pill came straight off the price column beside it and pushed "Billed X
    // every N months / what's included" into a wrap. Below the button it costs the row nothing.
    if (this.refs.sbCta) this.refs.sbCta.textContent = L.ctaWord || 'Add to basket';
    if (this.refs.sbSave) {
      const showSave = svTotal > 0;
      this.refs.sbSave.textContent = showSave ? `${L.saveWord || 'Save'} ${money(svTotal)}` : '';
      this.refs.sbSave.hidden = !showSave;
    }

    // --- Order-summary snapshot (feeds the floating-bar "what's included" modal) ---
    this.sum = {
      m, cadence: this.#cadence(m), ok, qty: this.qty, scent: this.scent, size: this.size,
      detSubRaw, fabOn, fabSubRaw, bottle: bottleWorth, cloth: clothWorth, mscRaw,
      kit: wantKit, msc: this.msc && giftFree, giftScent: this.giftScent, hq,
      mscMonths: giftPlan?.months || this.model.msc?.months || 2, giftPacks, tSubRaw,
      fabSize: this.addonMode ? this.fabSize : null,
      fabScent: this.addonMode ? this.fabScent : null,
    };
    if (this.refs.sumModal?.open) this.#fillSummary();

    this.#announce(`Your routine ${perMonth(tSubRaw, m)} per month`);

    // Broadcast the live scent so downstream copy (e.g. the closing-CTA plan card) can
    // name it. On the dishwasher PDP the laundry sheets ride as the optioned add-on, so
    // its scent is `fabScent`; on the laundry PDP it's the main `scent`.
    document.dispatchEvent(new CustomEvent('dip:scent', { detail: {
      scent: this.scent,
      addonScent: this.addonMode ? this.fabScent : null,
    } }));
  }

  #renderFromPrice() {
    if (!this.refs.fromPrice) return;
    // "from" = the entry price: the smallest size at its default cadence (e.g. 30 washes monthly = £12/mo).
    const size = (this.model.sizes || [])[0];
    const v = size && this.#variant(this.scent, size);
    const dm = size && this.#defaultMonths(size);
    const plan = v && v.plans[dm];
    if (plan) this.refs.fromPrice.textContent = `${perMonth(plan.raw, dm)}/month`;
  }

  // ---- Cart ----
  #lineItems(subscribe) {
    const m = this.months;
    const variant = this.variant;
    // `_dip_src` (hidden line property): the surface the add came from, for attribution. See dip-simple-buy.js.
    const src = `pdp-rich:${this.dataset.productId}`;
    const items = [{ id: Number(variant.id), quantity: this.qty, properties: { _dip_src: src } }];
    if (subscribe && variant.plans[m]) items[0].selling_plan = Number(variant.plans[m].id);

    if (this.fab && (this.model.fabcon || this.addonMode)) {
      const fv = this.#addonVariant();
      if (fv) {
        // Legacy fabcon rides the main quantity (washes-matched); an optioned add-on is its own pick.
        const line = { id: Number(fv.id), quantity: this.addonMode ? 1 : this.qty, properties: { _dip_src: `${src}:companion` } };
        if (subscribe && fv.plans[m]) line.selling_plan = Number(fv.plans[m].id);
        items.push(line);
      }
    }

    // Welcome kit. Bottle + cloth are ONE-TIME free-gift lines (no selling_plan, so they never
    // recur), zeroed by automatic Buy-X-Get-Y discounts; the gift subscription (MSC refill on the
    // laundry PDP, laundry sheets on the MSC PDP) is a real subscription on its own fixed cadence
    // whose first cycle is zeroed by a guard-managed code. Codes only apply at the initial checkout,
    // never to Recharge's recurring charges, so renewals bill at full price.
    // Legacy mode: everything gated on a qualifying subscription (>= threshold washes), never one-time.
    // Always-kit mode (kitConfig set — the MSC bundle): hardware ships at every size, on subscriptions
    // AND one-time orders, in per-size quantities; the threshold gates only the gift subscription.
    const kitOn = subscribe && this.#giftWashes() >= this.threshold && (this.kit || !this.kitRemovable);
    const g = this.model.gift || {};
    const gv = this.#giftVariant();
    const giftPlan = this.#giftPlan(gv);
    // Never post a gift pack we cannot zero: without its code it would bill in full behind a
    // "free" label. #giftSizeFor only offers coded sizes, so this is the second lock on the same door.
    const giftFree = !!(gv && giftPlan && this.#mscCodeFor(gv.size));
    if (this.alwaysKit) {
      const hq = this.#hwQty();
      if (g.bottleVariantId && hq.bottle) items.push({ id: Number(g.bottleVariantId), quantity: hq.bottle, _kit: 1 });
      if (g.clothVariantId && hq.cloth) items.push({ id: Number(g.clothVariantId), quantity: hq.cloth, _kit: 1 });
      // The gift pack is a SUBSCRIPTION line: the free first cycle is what seeds an MSC subscriber,
      // which is the point of the offer. Only a Basic code can zero a subscription line (a BXGY
      // cannot — verified 2026-07-27), so the pack's discount is FREE-MSC-LD1, applied by
      // dip-msc-guard.js when the cart qualifies. Bottle + cloth ride one-time and are zeroed by the
      // FREE-GIFT-LD1 automatic, which is triggered by the laundry line and so never touches the pack.
      if (kitOn && this.msc && giftFree) {
        items.push({ id: Number(gv.id), quantity: 1, selling_plan: Number(giftPlan.id), _kit: 1 });
      }
    } else if (kitOn) {
      if (g.bottleVariantId) items.push({ id: Number(g.bottleVariantId), quantity: 1, _kit: 1 });
      if (g.clothVariantId) items.push({ id: Number(g.clothVariantId), quantity: 1, _kit: 1 });
      if (this.msc && giftFree) {
        items.push({ id: Number(gv.id), quantity: 1, selling_plan: Number(giftPlan.id), _kit: 1 });
      }
    }
    return items;
  }

  async #addToCart(subscribe) {
    const cta = this.refs.cta;
    this.#clearError();
    const items = await dipDropKitDupes(this.#lineItems(subscribe));
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

    cta?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript'),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      if (data.status) {
        this.#showError(data.description || data.message || 'Could not add to cart.');
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        return;
      }

      // MSC "first pack free": handled by dip-msc-guard.js (loaded on every page), which applies the
      // code discount via cart/update.js when the cart qualifies (laundry >= threshold as a sub + MSC
      // sub line) and, crucially, REVOKES it the moment it doesn't (e.g. laundry deleted from the
      // cart). The guard reacts to the CartAddEvent dispatched below (bubbles as cart:update); the
      // config it needs is published in connectedCallback. See #publishMscGuardConfig.
      //
      // 2026-08-04: this used to await a SECOND round trip (GET /cart.js) before dispatching, so the
      // basket drawer only opened after two sequential requests, which read as a lag and then a snap.
      // The section HTML we just asked for already carries the authoritative count (cart-bubble.liquid
      // renders cart.item_count into [data-testid="cart-bubble"]), so read it from there and dispatch
      // straight away. The guard does its own /cart.js read when an event carries no cart resource.
      const itemCount = dipCartCountFromSections(data.sections);
      this.#announce('Added to your basket');
      if (subscribe && cta) {
        // innerHTML, not textContent: the label carries a struck was-price, and saving it as text would
        // restore the markup as visible tags.
        const lbl = this.refs.ctaLabel; const prev = lbl?.innerHTML;
        if (lbl) { lbl.dataset.busy = '1'; lbl.textContent = '✓ Added to basket'; }
        setTimeout(() => { if (lbl) { delete lbl.dataset.busy; lbl.innerHTML = prev; } }, 1600);
      }
      if (!subscribe) { this.onceAdded = true; this.render(); setTimeout(() => { this.onceAdded = false; this.render(); }, 2400); }
      this.dispatchEvent(new CartAddEvent(undefined, this.id || 'dip-buy-box', {
        source: 'dip-buy-box', itemCount: itemCount ?? items.length, productId: this.dataset.productId,
        variantId: this.variant.id, sections: data.sections,
      }));
    } catch (err) {
      console.error('[dip-buy-box] add to cart failed', err);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      cta?.removeAttribute('aria-busy');
    }
  }

  #showError(msg) { if (this.refs.error) { this.refs.error.textContent = msg; this.refs.error.hidden = false; } }
  #clearError() { if (this.refs.error) this.refs.error.hidden = true; }
  #announce(msg) { if (this.refs.liveRegion) this.refs.liveRegion.textContent = msg; }

  // ---- Gallery (scroll-snap + dots + thumbnails) ----
  // The carousel itself lives in @theme/dip-gallery, shared with the homepage bundle builder so the two
  // surfaces cannot drift apart.
  #initGallery() {
    initGallery({ track: this.refs.gallery, dots: this.refs.dots, thumbs: this.refs.thumbs, label: 'image' });
  }
  // ---- Stain carousel (desktop "next" + inline video play) ----
  #initCarousel() {
    const track = this.refs.vidTrack, next = this.refs.vidNext;
    next?.addEventListener('click', () => track?.scrollBy({ left: Math.round(track.clientWidth * 0.8), behavior: 'smooth' }));
    this.querySelectorAll('.dip-vid-thumb[data-video]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const src = btn.dataset.video;
        if (!src || btn.dataset.playing) return;
        const v = document.createElement('video');
        v.src = src; v.controls = true; v.autoplay = true; v.playsInline = true; v.className = 'dip-vid-el';
        btn.appendChild(v); btn.dataset.playing = '1';
        btn.querySelector('.dip-vid-play')?.style.setProperty('display', 'none');
      });
    });
  }

  // ---- Testimonial carousel (grid-stack crossfade, one visible) ----
  // Auto-rotates every 6s only while on screen; pauses on hover/focus; stops for
  // good once the user drives it (arrow or swipe) — their chosen quote stays put.
  #initQuotes() {
    const wrap = this.refs.quotes, track = this.refs.quotesTrack;
    if (!wrap || !track) return;
    const slides = [...track.querySelectorAll('.dip-quotes-slide')];
    if (slides.length < 2) return;

    const dots = [];
    if (this.refs.quotesDots) {
      slides.forEach((_, di) => {
        const d = document.createElement('span');
        if (di === 0) d.className = 'is-on';
        this.refs.quotesDots.appendChild(d);
        dots.push(d);
      });
    }

    let i = 0, timer = null, userDriven = false, advanced = false;
    const show = (n) => {
      i = (n + slides.length) % slides.length;
      slides.forEach((sl, si) => {
        const on = si === i;
        sl.classList.toggle('is-on', on);
        sl.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      dots.forEach((d, di) => d.classList.toggle('is-on', di === i));
    };
    const stop = () => { if (timer) { clearTimeout(timer); timer = null; } };
    // First advance is quick (1s) so it's instantly obvious there are more reviews; then every 3s.
    const tick = () => { advanced = true; show(i + 1); timer = setTimeout(tick, 3000); };
    const start = () => {
      if (userDriven || timer || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      timer = setTimeout(tick, advanced ? 3000 : 1000);
    };
    // Once user-driven, announce changes (aria-live stays off during auto-rotation to avoid SR spam).
    const takeOver = () => { userDriven = true; stop(); track.setAttribute('aria-live', 'polite'); };

    this.refs.quotesPrev?.addEventListener('click', () => { takeOver(); show(i - 1); });
    this.refs.quotesNext?.addEventListener('click', () => { takeOver(); show(i + 1); });
    wrap.addEventListener('mouseenter', stop);
    wrap.addEventListener('mouseleave', start);
    wrap.addEventListener('focusin', stop);
    wrap.addEventListener('focusout', start);

    // Swipe: horizontal drags flip the quote (track has touch-action:pan-y, so
    // vertical page scroll still wins); mostly-vertical gestures are ignored.
    let x0 = null, y0 = null;
    track.addEventListener('pointerdown', (e) => { x0 = e.clientX; y0 = e.clientY; }, { passive: true });
    track.addEventListener('pointercancel', () => { x0 = null; });
    track.addEventListener('pointerup', (e) => {
      if (x0 == null) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.5) { takeOver(); show(dx < 0 ? i + 1 : i - 1); }
    });

    // Rotate only while visible (also serves as the initial start()).
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => entries.forEach((en) => (en.isIntersecting ? start() : stop())),
        { threshold: 0.4 }
      ).observe(wrap);
    } else start();
  }

  // ---- Tabsets (full-INCI-by-formulation inside the Ingredients accordion) ----
  #initTabsets() {
    this.querySelectorAll('[data-dip-tabs]').forEach((set) => {
      const tabs = [...set.querySelectorAll('[role="tab"]')];
      const panels = tabs.map((t) => set.querySelector(`#${CSS.escape(t.getAttribute('aria-controls'))}`));
      const select = (n) => {
        const idx = (n + tabs.length) % tabs.length;
        tabs.forEach((t, ti) => {
          const on = ti === idx;
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          if (panels[ti]) { panels[ti].hidden = !on; panels[ti].classList.toggle('is-hidden', !on); }
        });
      };
      tabs.forEach((t, ti) => {
        t.addEventListener('click', () => select(ti));
        t.addEventListener('keydown', (e) => {
          const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          const next = (ti + dir + tabs.length) % tabs.length;
          select(next); tabs[next].focus();
        });
      });
    });
  }

  // ---- "How your subscription works" link → open the modal (native <dialog>) ----
  #initHowLink() {
    const link = this.refs.howLink, modal = this.refs.subModal;
    if (!link) return;
    if (!modal) { link.hidden = true; return; }
    const supportsModal = typeof modal.showModal === 'function';
    const open = () => { supportsModal ? modal.showModal() : modal.setAttribute('open', ''); };
    const close = () => { supportsModal ? modal.close() : modal.removeAttribute('open'); };
    link.addEventListener('click', open);
    this.refs.subModalClose?.addEventListener('click', close);
    // Click on the backdrop (the dialog element itself, outside the card) closes it.
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
  }

  // ---- Welcome-kit FLIP: summary mini-thumbs fly into their body rows on expand ----
  // Each mini pairs with the body icon well showing the SAME product, matched on data-flip-key so
  // adding, removing or reordering rows cannot silently mis-pair them (position was the old pairing,
  // and it sent the bottle flying into the gift's icon whenever a row moved). Falls back to position
  // for any markup that predates the keys. On open we animate each body icon FROM its mini's
  // position/scale to rest; minis hide via CSS ([open] rule, opacity so layout survives measurement).
  // Reduced motion / no pairs: plain expand.
  #initKitFlip() {
    const kit = this.refs.kit;
    const minis = this.refs.kitMinis;
    if (!kit || !minis || kit.tagName !== 'DETAILS' || !kit.animate) return;
    kit.addEventListener('toggle', () => {
      if (!kit.open || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const miniEls = [...minis.querySelectorAll('.dip-kit-mini')];
      const bodyIcs = [...kit.querySelectorAll('.dip-kit-body .dip-kit-ic')];
      miniEls.forEach((m, i) => {
        const key = m.dataset.flipKey;
        const target = (key && bodyIcs.find((el) => el.dataset.flipKey === key)) || bodyIcs[i];
        if (!target) return;
        const a = m.getBoundingClientRect();
        const b = target.getBoundingClientRect();
        if (!a.width || !b.width) return;
        target.animate(
          [
            { transform: `translate(${a.left - b.left}px, ${a.top - b.top}px) scale(${a.width / b.width})` },
            { transform: 'none' },
          ],
          { duration: 380, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', delay: i * 40, fill: 'backwards' }
        );
      });
    });
  }

  // ---- Savings (same method as the bundle builder) ----------------------------
  //   1. subscription  this basket one-time minus its plan price
  //   2. bigger packs  what that volume would cost at the SMALLEST size's unit rate, one-time both
  //                    sides. Clamped at 0 per line so a size that is not better value per unit can
  //                    never read as a saving.
  //   3. free gift     the welcome kit's worth, only while it is actually in the order
  #unitRateOf(list) {
    const units = (v) => Number(v.washes) || Number(v.packs) || 0;
    const base = (list || []).filter((v) => units(v) > 0 && v.otRaw).sort((a, b) => units(a) - units(b))[0];
    return base ? base.otRaw / units(base) : 0;
  }

  #packSaveOf(list, variant, qty) {
    if (!variant) return 0;
    const units = Number(variant.washes) || Number(variant.packs) || 0;
    const rate = this.#unitRateOf(list);
    if (!units || !rate) return 0;
    return Math.max(0, units * rate - variant.otRaw) * (qty || 1);
  }

  // ---- Order-summary modal (floating bar → "what's included") ----
  // The laundry offer adds several lines from one tap (plan + matched fabcon + free kit + an MSC
  // refill subscription that renews at full price). This lays all of that out — especially the
  // recurring refill line — so nothing is a surprise before checkout. Body is rebuilt each open
  // (and on re-render while open) from this.sum, the snapshot render() writes.
  #initSummary() {
    const modal = this.refs.sumModal;
    // Any [data-sum-open] opens it — the mobile floating-bar price AND the desktop "See full
    // breakdown" link under the in-plan recap (so the itemised summary is reachable on desktop too).
    // NB: the sticky bar is reparented to <body> during init (see #initStickyBar), so it can be
    // outside `this` by now — refs.sumOpen (captured at connect) covers it; dedupe via Set.
    const triggers = [...new Set([this.refs.sumOpen, ...this.querySelectorAll('[data-sum-open]')].filter(Boolean))];
    if (!modal || !triggers.length) return;
    const supportsModal = typeof modal.showModal === 'function';
    const open = () => { this.#fillSummary(); supportsModal ? modal.showModal() : modal.setAttribute('open', ''); };
    const close = () => { supportsModal ? modal.close() : modal.removeAttribute('open'); };
    triggers.forEach((t) => t.addEventListener('click', open));
    this.refs.sumModalClose?.addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    // Commit straight from the summary. Close first: the cart drawer opens on CartAddEvent and the
    // dialog's top layer would otherwise sit over it; any add error still surfaces on the form.
    this.refs.sumAdd?.addEventListener('click', () => { close(); this.#addToCart(true); });
  }

  #fillSummary() {
    const el = this.refs.sumBody, s = this.sum;
    if (!el || !s) return;
    const L = this.labels;
    const lines = [
      `<li><span class="dip-sum-t">${L.main || 'Laundry Detergent Sheets'}<small>${s.size}${s.scent ? ` · ${s.scent}` : ''}</small></span><b>${money(s.detSubRaw * s.qty)}</b></li>`,
    ];
    const addonSumSub = s.fabSize ? `${s.fabSize}${s.fabScent ? ` · ${s.fabScent}` : ''}` : (L.addonSumSub || 'added to the same delivery');
    const addonQty = this.addonMode ? 1 : s.qty;
    if (s.fabOn) lines.push(`<li><span class="dip-sum-t">${L.addon || 'Fabric Conditioner Sheets'}<small>${addonSumSub}</small></span><b>${money(s.fabSubRaw * addonQty)}</b></li>`);
    let html = `<ul class="dip-sum-lines">${lines.join('')}</ul>`;

    if ((s.ok || this.alwaysKit) && s.kit) {
      const kit = [];
      const bq = s.hq ? s.hq.bottle : 1;
      const cq = s.hq ? s.hq.cloth : 1;
      if (s.bottle && bq) kit.push(`<li><span class="dip-sum-t">${bq > 1 ? bq + '× ' : ''}${L.bottle || 'Refillable spray bottle'}</span><b class="dip-sum-free"><s>${moneyTidy(s.bottle)}</s> FREE</b></li>`);
      if (s.cloth && cq) kit.push(`<li><span class="dip-sum-t">${cq > 1 ? cq + '× ' : ''}${L.cloth || 'Dip Microfibre Cloth'}</span><b class="dip-sum-free"><s>${moneyTidy(s.cloth)}</s> FREE</b></li>`);
      const giftQty = s.giftPacks > 1 ? `${s.giftPacks}× ` : '';
      if (s.ok && s.msc) kit.push(`<li><span class="dip-sum-t">${giftQty}${L.msc || 'Multi-Purpose Refills'}${s.giftScent ? ` · ${s.giftScent}` : ''}<small>First delivery free, then ${moneyTidy(s.mscRaw)} ${this.#cadence(s.mscMonths)} · cancel anytime</small></span><b class="dip-sum-free">1st FREE</b></li>`);
      if (kit.length) html += `<div class="dip-sum-kit"><p class="dip-sum-kit-h">Welcome kit, free with your first order</p><ul class="dip-sum-lines">${kit.join('')}</ul></div>`;
    }

    let renew = `Then <b>${money(s.tSubRaw)}</b> ${s.cadence}`;
    if (s.ok && s.msc && s.kit) renew += `, plus ${(L.msc || 'the gift subscription').toLowerCase()} ${moneyTidy(s.mscRaw)} ${this.#cadence(s.mscMonths)} after the first pack`;
    html += `<div class="dip-sum-total"><div class="dip-sum-today"><span>Billed today</span><b>${money(s.tSubRaw)}</b></div><p class="dip-sum-renew">${renew}.</p></div>`;
    html += `<p class="dip-sum-note">Pause, skip or cancel anytime, no minimum term.</p>`;
    el.innerHTML = html;
  }

  // ---- Floating bar ----
  // POSITION: `position:fixed; bottom:0` anchors to Chrome Android's LAYOUT viewport, which stays the
  // shorter (address-bar-visible) height. When you scroll down and the address bar hides, the visible
  // area grows taller than the layout viewport, so bottom:0 ends up ABOVE the true visible bottom
  // (mid-content); scrolling up shrinks it back (correct) — the exact down/up asymmetry seen. Fix: nudge
  // the bar to the real visible bottom with a GPU transform, recomputed ONLY on visualViewport `resize`
  // (i.e. when the address bar actually moves) — NOT per scroll frame, which is what jittered before.
  // REVEAL: window.scrollY vs a cached document Y, wide hysteresis, shown via opacity (see CSS).
  // The bar's behaviour (including the address-bar compensation) lives in @theme/dip-sticky-bar,
  // shared with the bundle builder so the two cannot drift apart.
  #initSticky() {
    // Anchor on the CTA, not the gallery: the bar's job is to carry the buy button once the
    // real one has scrolled away. Anchored to the gallery it revealed ~3/4 past the images,
    // screens BEFORE the button (Charlie, on device, 2026-08-26). Gallery stays the fallback.
    this.stickyBarEl = initStickyBar({ bar: this.refs.stickybar, anchor: this.refs.cta || this.querySelector('.dip-media') });
  }

  disconnectedCallback() { this.stickyBarEl?.remove(); }
}

// Retained helper components for the below-fold dishwasher sections (dip-pdp-tabs etc.).
class DipGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[ref="slide[]"]')];
    this.thumbs = [...this.querySelectorAll('[ref="thumb[]"]')];
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.show(Number(t.dataset.index))));
  }
  show(index) {
    this.slides.forEach((s) => s.classList.toggle('is-active', Number(s.dataset.index) === index));
    this.thumbs.forEach((t) => {
      const active = Number(t.dataset.index) === index;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('dip-buy-box')) customElements.define('dip-buy-box', DipBuyBox);
if (!customElements.get('dip-gallery')) customElements.define('dip-gallery', DipGallery);

// Reveal-on-scroll: resting state visible; animate only what scrolls into view.
// Elements that WILL animate get .dip-pre first (CSS pre-hides them) so they never
// paint visible and then flash to opacity 0 when the animation kicks in — that
// flash is what read as "jerky" while scrolling. Above-fold elements are never
// observed, never pre-hidden, never animated.
// The same block lives in dip-home.js and dip-pdp-below.js so any page animates whichever
// of the three it loads; the flag makes the first one to run the only one that binds.
(() => {
  if (window.__dipRevealInit) return;
  window.__dipRevealInit = true;
  const reveal = () => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries, obs) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); obs.unobserve(e.target); } }),
      { rootMargin: '0px 0px -4% 0px', threshold: 0.05 }
    );
    document.querySelectorAll('.dip-reveal').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (!(r.top < window.innerHeight && r.bottom > 0)) { el.classList.add('dip-pre'); io.observe(el); }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reveal, { once: true });
  else reveal();
})();
